import {UserApiDto, UserCreateApiDto, UserUpdateApiDto} from "../../api/model/rest";
import UserData from "./UserData";
import UserConsentData, {UserConsentType} from "./UserConsentData";
import {none, option} from "ts-option";
import Address from "../address/Address";
import JsonConverter from "../../api/helper/JsonConverter";
import {UserRole} from "./UserRole";
import Phone, {PhoneTypeEnum} from "../phone/Phone";
import {UserPermission} from "./UserPermission";

/**
 * User model.
 */
export default class User {
	/**
	 * String using to search a user.
	 */
	public readonly searchString: string;

	/**
	 * @param id user's identifier.
	 * @param data User data object.
	 * @param consents user consents data.
	 * @param disabled user disabled flag
	 * @param xeroUser
	 */
	constructor(public readonly id: string,
		public readonly data: UserData,
		public readonly consents: UserConsentData[],
		public readonly disabled: boolean,
		public readonly xeroUser: boolean) {
		this.searchString = `${data.firstName} ${data.lastName}`.toLowerCase()
	}

	/**
	 * @param {boolean} withStatus add disabled description if true
	 * @returns {string} user display name.
	 */
	public getDisplayName(withStatus?: boolean): string {
		const displayName = `${this.data.firstName} ${this.data.lastName}`;
		return `${withStatus && this.disabled ? `${displayName} (disabled)` : displayName}`
	}

	/**
	 *
	 * @param {boolean} withStatus add disabled description if true
	 * @returns {string} assignee display name.
	 */
	public getAssigneeDisplayName(withStatus?: boolean): string {
		const displayName = `${this.data.firstName} ${this.data.lastName.charAt(0)}.`;
		return `${withStatus && this.disabled ? `${displayName} (disabled)` : displayName}`
	}

	/**
	 * @returns {string} first letter of first name + first letter of lastname.
	 */
	public getInitials(): string {
		return `${this.data.firstName.charAt(0).concat(this.data.lastName.charAt(0))}`
	}

	/**
	 * Check if user have given role.
	 * @param {string} role
	 * @return {boolean}
	 */
	haveRole(role: string): boolean {
		return this.data.roles.indexOf(role) > -1;
	}

	/**
	 * Checks if user has one of allowed roles.
	 * @param {string[]} roles
	 * @returns {boolean}
	 */
	hasOneOfRoles(roles: string[]): boolean {
		return roles.some(role => this.haveRole(role));
	}

	/**
	 * Render user initials. Concat first letter of the 'firstName' with first name of the 'lastName'.
	 * @return {string}
	 */
	renderUserInitials() {
		return `${this.data.firstName[0]}${this.data.lastName[0]}`.toUpperCase()
	}

	/**
	 * Check if user is sales engineer.
	 * @return {boolean}
	 */
	public isSalesEngineer(): boolean {
		return this.haveRole(UserRole.SalesEngineer);
	}

	public isAgent(): boolean {
		return this.haveRole(UserRole.Agent);
	}

	public isBusinessDevelopmentPartner(): boolean {
		return this.haveRole(UserRole.BusinessDevelopmentPartner);
	}

	public isThirdPartyEngineer(): boolean {
		return this.haveRole(UserRole.ThirdPartyEngineer);
	}

	public isAdmin(): boolean {
		return this.haveRole(UserRole.Admin);
	}

	public hasAcceptedToS(): boolean {
		return this.consents.some(ucd => ucd.userConsentType === UserConsentType.Crm);
	}

	/**
	 * Checks if user has one of allowed permissions.
	 * @param {string[]} permissions
	 * @returns {boolean} returns true if user has atleast one allowed role.
	 */
	hasPermissions(permissions: string[]) {
		return this.data.permissions.some(r => permissions && permissions.indexOf(r) >= 0);
	}

	hasLoginPerm(): boolean {
		return this.hasPermissions([UserPermission.CrmLogin])
	}

	hasRewardsManagementPerm(): boolean {
		return this.hasPermissions([UserPermission.RewardsManagement]);
	}

	hasPartnerQuoteEmailTemplatePerm(): boolean {
		return this.hasPermissions([UserPermission.PartnerQuoteEmailTemplate]);
	}

	hasContactArchivePerm(): boolean {
		return this.hasPermissions([UserPermission.ContactArchive]);
	}

	canFetchData(): boolean {
		return this.hasAcceptedToS() && this.hasLoginPerm()
	}

	/**
	 * Checks if user can be assigned to lead/job as watcher.
	 * @returns {boolean}
	 */
	canBeWatcher(): boolean {
		return this.isAdmin() || this.isAgent() || this.isBusinessDevelopmentPartner();
	}

	static fromJson(json: UserApiDto): User {
		// users/me endpoint returns roles with prefix 'ROLE_'
		const roles = json.roles.map(role => role.replace("ROLE_", ""));
		// users/me endpoint returns permissions with prefix 'PERM_'
		const permissions = json.permissions.map(permission => permission.replace("PERM_", ""));
		const userAddress = json.homeAddress ? option(Address.fromJson(json.homeAddress)) : none;
		//TODO  change national number to actual national number when backend is ready.
		const mobileNumber = json.mobileNumber ?
			option(new Phone(json.mobileNumber, json.mobileNumber, option(PhoneTypeEnum.Mobile), option(true))) : none;
		const data = new UserData(json.username,
			json.firstName, json.lastName,
			roles,
			permissions,
			json.organizationName,
			userAddress,
			mobileNumber,
			option(json.profileUrl),
			option(json.brandId),
			none,
			option(json.bioText),
			option(json.photoId),
			option(json.partnerBrandId),
			option(json.licenceNumber));
		const userConsent = json.consents && json.consents.map(UserConsentData.fromJson);
		return new User(json.id, data, userConsent, json.disabled, json.xeroUser);
	}

	/**
	 * Data for creating new user.
	 * @param {string} password
	 * @param {UserData} data
	 * @return {UserCreateApiDto}
	 */
	public static toUserCreateJson(password: string, data: UserData): UserCreateApiDto {
		const json: UserCreateApiDto = {
			password: password,
			username: data.username,
			firstName: data.firstName,
			lastName: data.lastName,
			roles: data.roles,
			permissions: data.permissions,
			homeAddress: data.homeAddress.map(ha => ha.toJson()).orNull,
			mobileNumber: data.mobileNumber.map(p => p.internationalPhoneNumber).orNull,
			profileUrl: data.profileUrl.orNull,
			brandId: data.brandId.orNull,
			bioText: data.bioText.orNull,
			photoId: data.photoId.orNull,
			partnerBrandId: data.partnerBrandId.orNull,
			licenceNumber: data.licenceNumber.orNull
		};
		return JsonConverter.removeEmptyFields(json);
	}

	/**
	 * Data for updating user.
	 * @param {UserData} data
	 * @return {UserUpdateApiDto}
	 */
	public static toUserUpdateJson(data: UserData): UserUpdateApiDto {
		const json: UserUpdateApiDto = {
			firstName: data.firstName,
			lastName: data.lastName,
			roles: data.roles,
			permissions: data.permissions,
			homeAddress: data.homeAddress.map(ha => ha.toJson()).orNull,
			mobileNumber: data.mobileNumber.map(p => p.internationalPhoneNumber).orNull,
			profileUrl: data.profileUrl.orNull,
			brandId: data.brandId.orNull,
			bioText: data.bioText.orNull,
			photoId: data.photoId.orNull,
			partnerBrandId: data.partnerBrandId.orNull,
			licenceNumber: data.licenceNumber.orNull
		};
		return JsonConverter.removeEmptyFields(json);
	}

	copy({data = this.data, consents = this.consents, disabled = this.disabled}) {
		return new User(this.id, data, consents, disabled, this.xeroUser)
	}

	static compare(a: User, b: User): number {
		const result = a.data.firstName.localeCompare(b.data.firstName);
		return result == 0 ? a.id.localeCompare(b.id) : result
	}
}